import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatIstDateTimeFromParts, formatIstDateKey, isIstToday, useIstClock } from './ist-datetime';
import { fmtRsPerShare } from './trading-rules';
import IstClock from './IstClock';
import './NormalTrading.css';
import './NormalTradingV1.css';
import './NormalTradingV1Sold.css';

function fmtRs(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const sign = v >= 0 ? '+' : '';
  return `${sign}₹${Math.abs(v).toFixed(v % 1 === 0 ? 0 : 2)}`;
}

function soldRowSortMs(row) {
  if (row?.lastUpdated != null && Number.isFinite(Number(row.lastUpdated))) {
    return Number(row.lastUpdated);
  }
  if (row?.savedAt) {
    const t = Date.parse(row.savedAt);
    if (Number.isFinite(t)) return t;
  }
  if (row?.sellDateTime) {
    const t = Date.parse(row.sellDateTime);
    if (Number.isFinite(t)) return t;
  }
  const composed = `${row?.sellDate || ''} ${row?.sellTimeLabel || ''}`.trim();
  if (composed) {
    const t = Date.parse(composed);
    if (Number.isFinite(t)) return t;
  }
  return 0;
}

const SOLD_POLL_MS = 10000;

export default function NormalTradingV1Sold() {
  const [data, setData] = useState(null);
  const [filterDate, setFilterDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const params = date ? { date } : {};
      const res = await axios.get('/api/normal-v1/sold-history', { params, timeout: 15000 });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load sold history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filterDate || undefined);
    const timer = setInterval(() => load(filterDate || undefined), SOLD_POLL_MS);
    return () => clearInterval(timer);
  }, [filterDate, load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const list = filterDate
      ? (data.soldHistory || [])
      : (data.allHistory || data.soldHistory || []);
    return [...list].sort((a, b) => soldRowSortMs(b) - soldRowSortMs(a));
  }, [data, filterDate]);

  const dailyTotals = data?.dailyTotals || [];
  const stockTotals = data?.stockTotals || [];
  const [showStockTotals, setShowStockTotals] = useState(false);

  const todayDate = data?.todayDate || '';
  const todayCount = data?.counts?.today ?? (data?.soldToday || []).length;
  const totalCount = data?.counts?.total ?? rows.length;
  const totalPnl = filterDate
    ? (data?.filteredPnl ?? rows.reduce((s, r) => s + (Number(r.pnl) || 0), 0))
    : (data?.totalPnl ?? rows.reduce((s, r) => s + (Number(r.pnl) || 0), 0));
  const dates = data?.dates || [];
  const liveNow = useIstClock();
  const todayLabel = todayDate ? (isIstToday(todayDate) ? liveNow : formatIstDateKey(todayDate)) : '—';

  return (
    <div className="nt-desktop nt-v1 nt-v1-sold-page nt-no-sidebar">
      <main className="nt-main nt-v1-sold-main">
        <header className="nt-v1-sold-head">
          <div>
            <h1>Sold history</h1>
            <p className="nt-v1-sold-sub">
              Today ({todayLabel}): <strong>{todayCount}</strong> sell(s) · All time: <strong>{totalCount}</strong>
              {data?.ledgerPersisted ? ' · saved on server' : ''}
            </p>
            <Link to="/NormalTrading" className="nt-v1-btn nt-v1-sold-back">← Back to live board</Link>
            <Link to="/ledger/normal" className="nt-v1-btn">Buy/Sell Ledger</Link>
          </div>
          <div className="nt-v1-sold-filters">
            <IstClock />
            <label>
              <span>Date &amp; time</span>
              <select
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              >
                <option value="">All dates</option>
                {todayDate && <option value={todayDate}>Today — {todayLabel}</option>}
                {dates.filter((d) => d !== todayDate).map((d) => (
                  <option key={d} value={d}>{formatIstDateKey(d)}</option>
                ))}
              </select>
            </label>
            <button type="button" className="nt-v1-btn" onClick={() => load(filterDate || undefined)} disabled={loading}>
              {loading ? '…' : 'Refresh'}
            </button>
          </div>
        </header>

        {error && <p className="nt-v1-sold-error" role="alert">{error}</p>}

        <div className="nt-v1-sold-summary">
          <span>Showing <strong>{rows.length}</strong> sell(s){filterDate ? ` on ${formatIstDateKey(filterDate)}` : ' — all dates'}</span>
          <span>Total P&amp;L: <strong className={totalPnl >= 0 ? 'pos' : 'neg'}>{fmtRs(totalPnl)}</strong></span>
          <span className="nt-v1-sold-never">History is never deleted — restarts &amp; resets keep all profitable sells</span>
        </div>

        {dailyTotals.length > 0 && (
          <div className="nt-v1-sold-daily-totals">
            <h2>Per-day profit (permanent)</h2>
            <ul>
              {dailyTotals.map((d) => (
                <li key={d.date}>
                  <button
                    type="button"
                    className={filterDate === d.date ? 'active' : ''}
                    onClick={() => setFilterDate((prev) => (prev === d.date ? '' : d.date))}
                  >
                    <strong>{d.dateTime || formatIstDateKey(d.date)}</strong>
                    <span>{d.count} sell(s)</span>
                    <em className="pos">{fmtRs(d.totalPnl)}</em>
                    {d.totalInvested > 0 && (
                      <span className="nt-v1-sold-day-extra">
                        Turnover ₹{Math.round(d.totalInvested).toLocaleString('en-IN')}
                        {d.maxCapitalUsed > 0 ? ` · Capital used ₹${Math.round(d.maxCapitalUsed).toLocaleString('en-IN')}` : ''}
                        {d.totalBrokerage > 0 ? ` · Charges ₹${d.totalBrokerage.toFixed(0)}` : ''}
                        {d.totalNet !== d.totalPnl ? ` · Net ${fmtRs(d.totalNet)}` : ''}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            {filterDate && (
              <button type="button" className="nt-v1-sold-clear-filter" onClick={() => setFilterDate('')}>
                Show all dates again
              </button>
            )}
          </div>
        )}

        {stockTotals.length > 0 && (
          <div className="nt-v1-sold-daily-totals">
            <h2>
              Per-stock earnings (all time)
              <button
                type="button"
                className="nt-v1-btn"
                style={{ marginLeft: 12 }}
                onClick={() => setShowStockTotals((v) => !v)}
              >
                {showStockTotals ? 'Hide' : `Show ${stockTotals.length} stocks`}
              </button>
            </h2>
            {showStockTotals && (
              <div className="nt-v1-sold-table-wrap">
                <table className="nt-v1-sold-table">
                  <thead>
                    <tr>
                      <th>Stock</th>
                      <th>Sells</th>
                      <th>Invested (total)</th>
                      <th>Gross earn</th>
                      <th>Net earn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockTotals.map((s) => (
                      <tr key={s.stock}>
                        <td><strong>{s.stock}</strong></td>
                        <td>{s.count}</td>
                        <td>₹{Math.round(s.totalInvested).toLocaleString('en-IN')}</td>
                        <td className={s.totalPnl >= 0 ? 'pos' : 'neg'}>{fmtRs(s.totalPnl)}</td>
                        <td className={s.totalNet >= 0 ? 'pos' : 'neg'}>{fmtRs(s.totalNet)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {loading && !rows.length ? (
          <p className="nt-v1-sold-empty">Loading sold history…</p>
        ) : !rows.length ? (
          <p className="nt-v1-sold-empty">No profitable sells recorded yet.</p>
        ) : (
          <div className="nt-v1-sold-table-wrap">
            <table className="nt-v1-sold-table">
              <thead>
                <tr>
                  <th>Buy date &amp; time</th>
                  <th>Sell date &amp; time</th>
                  <th>Stock</th>
                  <th>Qty</th>
                  <th>Buy ₹</th>
                  <th>Sell ₹</th>
                  <th>Invested ₹</th>
                  <th>Earn/Loss per share</th>
                  <th>Gross P&amp;L</th>
                  <th>Charges (buy)</th>
                  <th>Charges (sell)</th>
                  <th>Net P&amp;L</th>
                  <th>Capital used</th>
                  <th>Capital left</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.sellDate}-${row.stock}-${row.sellTimeLabel}-${row.sellPrice}`}>
                    <td>{row.buyDateTime || '—'}</td>
                    <td>{row.sellDateTime || formatIstDateTimeFromParts(row.sellDate || todayDate, row.sellTimeLabel)}</td>
                    <td>
                      <strong>{row.stock}</strong>
                      {row.mirror ? <em className="nt-v1-sold-mirror">mirror</em> : null}
                    </td>
                    <td>{row.qty ?? 50}</td>
                    <td>₹{Number(row.buyPrice || 0).toFixed(2)}</td>
                    <td>₹{Number(row.sellPrice || 0).toFixed(2)}</td>
                    <td>{row.invested != null ? `₹${Number(row.invested).toFixed(2)}` : (row.buyPrice ? `₹${(Number(row.buyPrice) * (row.qty ?? 50)).toFixed(2)}` : '—')}</td>
                    <td className={Number(row.move) >= 0 ? 'pos' : 'neg'}>{fmtRsPerShare(row.move)}</td>
                    <td className={Number(row.pnl) >= 0 ? 'pos' : 'neg'}>{fmtRs(row.pnl)}</td>
                    <td>{row.chargesBuy != null ? `₹${Number(row.chargesBuy).toFixed(2)}` : (row.brokerageBuy != null ? `₹${Number(row.brokerageBuy).toFixed(2)}` : '—')}</td>
                    <td>{row.chargesSell != null ? `₹${Number(row.chargesSell).toFixed(2)}` : (row.brokerageSell != null ? `₹${Number(row.brokerageSell).toFixed(2)}` : '—')}</td>
                    <td className={Number(row.netPnl ?? row.pnl) >= 0 ? 'pos' : 'neg'}>{row.netPnl != null ? fmtRs(row.netPnl) : '—'}</td>
                    <td>{row.capitalUsed != null ? `₹${Number(row.capitalUsed).toFixed(0)}` : '—'}</td>
                    <td>{row.capitalLeft != null ? `₹${Number(row.capitalLeft).toFixed(0)}` : '—'}</td>
                    <td className="nt-v1-sold-reason">{row.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
